<template>
  <v-container fluid class="pa-4 bg-light">
    <!-- Header Control Bar -->
    <v-card class="mb-6 elevation-2 overflow-hidden" rounded="lg">
      <v-sheet class="calendar-header-banner px-6 py-4 d-flex align-center justify-space-between flex-wrap" dark>
        <div class="d-flex align-center">
          <v-avatar color="primary lighten-1" size="48" class="mr-3 elevation-1">
            <v-icon dark size="28">mdi-calendar-month</v-icon>
          </v-avatar>
          <div>
            <!-- Clickable Month/Year Selector Menu -->
            <v-menu
              ref="menu"
              v-model="menuOpen"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="d-flex align-center cursor-pointer select-month-trigger"
                >
                  <h1 class="headline font-weight-bold text-white mb-0 capitalize mr-2">
                    {{ title }}
                  </h1>
                  <v-icon dark small class="mt-1">mdi-menu-down</v-icon>
                </div>
              </template>
              <v-date-picker
                v-model="pickerMonth"
                type="month"
                no-title
                scrollable
                locale="es"
                color="primary"
                @input="onMonthPickerInput"
              ></v-date-picker>
            </v-menu>
            <span class="text-subtitle-2 text-white-muted font-weight-medium d-block">
              Calendario de Cajas Diarias (Vuetify Native)
            </span>
          </div>
        </div>

        <div class="d-flex align-center my-2">
          <v-btn
            icon
            dark
            class="mr-2 border-white-op cursor-pointer"
            @click="prev"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          
          <v-btn
            outlined
            dark
            class="mr-2 text-capitalize border-white-op font-weight-bold"
            @click="setToday"
          >
            Hoy
          </v-btn>

          <v-btn
            icon
            dark
            class="mr-4 border-white-op cursor-pointer"
            @click="next"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>

          <v-btn
            color="white"
            class="primary--text text-capitalize font-weight-bold ml-2 shadow-sm"
            @click="refreshCurrentMonth"
            :loading="loadingMonth"
          >
            <v-icon left>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </v-sheet>
    </v-card>

    <!-- Vuetify Native Calendar Sheet -->
    <v-card class="elevation-3 pa-2 mb-4" rounded="lg" style="background: #ffffff; border: 1px solid rgba(0,0,0,0.06);">
      <v-calendar
        ref="calendar"
        v-model="focus"
        color="primary"
        type="month"
        locale="es"
        :short-weekdays="false"
        @change="updateRange"
        style="min-height: 750px;"
      >
        <!-- Custom Day Content Slot -->
        <template v-slot:day="{ date }">
          <div 
            class="calendar-day-container d-flex flex-column justify-space-between h-100 pa-2 cursor-pointer"
            @click="openDayDialog(date)"
          >
            <!-- Cell Top Bar -->
            <div class="d-flex justify-space-between align-center mb-1">
              <span v-if="isToday(date)" class="today-tag shadow-sm">Hoy</span>
              <span v-else></span>
              <span :class="['day-num-badge shadow-sm', { 'is-today-badge': isToday(date) }]">
                {{ getDayNumber(date) }}
              </span>
            </div>

            <!-- Cell Body (Compact Summary) -->
            <div class="flex-grow-1 my-2 overflow-y-auto" style="max-height: 110px;">
              <!-- Loading state for that date -->
              <div v-if="daysData[date] && daysData[date].loading" class="d-flex flex-column align-center justify-center py-4">
                <v-progress-circular indeterminate color="primary" size="20" width="2"></v-progress-circular>
              </div>

              <template v-else-if="daysData[date]">
                <!-- Estado A: Con transacciones -->
                <div v-if="daysData[date].hasData" class="w-100">
                  <!-- Pagos Total Summary -->
                  <div 
                    v-if="totalPayments(date) > 0"
                    class="cell-summary-row d-flex align-center primary--text font-weight-bold"
                  >
                    <v-icon x-small color="primary" class="mr-1">mdi-cash-register</v-icon>
                    <span>$ {{ totalPayments(date) | formatToTwoDecimals }}</span>
                  </div>

                  <!-- Subsidios Total Summary -->
                  <div 
                    v-if="daysData[date].subsidiosKg > 0"
                    class="cell-summary-row d-flex align-center orange--text text--darken-3 font-weight-bold"
                  >
                    <v-icon x-small color="orange darken-3" class="mr-1">mdi-scale-balance</v-icon>
                    <span>{{ daysData[date].subsidiosKg | formatToThreeDecimals }} kg</span>
                  </div>
                </div>

                <!-- Estado B: Sin transacciones (Caja no generada) -->
                <div v-else class="d-flex flex-column align-center justify-center py-2 h-100">
                  <span class="no-mov-text text-caption grey--text text--lighten-1 italic">Sin movimientos</span>
                </div>
              </template>
            </div>

            <!-- Cell Footer (Indicator) -->
            <div 
              v-if="daysData[date] && !daysData[date].loading && daysData[date].hasData" 
              class="d-flex justify-end pt-1"
            >
              <v-icon x-small color="primary">mdi-eye-circle-outline</v-icon>
            </div>
          </div>
        </template>
      </v-calendar>
    </v-card>

    <!-- Day Detail Dialog Modal -->
    <v-dialog v-model="dialogOpen" max-width="500px" transition="dialog-bottom-transition">
      <v-card v-if="selectedDate" class="overflow-hidden" rounded="lg">
        <!-- Dialog Header Banner -->
        <v-sheet class="calendar-header-banner px-6 py-4 d-flex align-center justify-space-between" dark>
          <div class="d-flex align-center">
            <v-avatar color="primary lighten-1" size="40" class="mr-3 elevation-1">
              <v-icon dark size="22">mdi-calendar-edit</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h6 font-weight-bold text-white mb-0">
                Detalle de Caja Diaria
              </h2>
              <span class="text-caption text-white-muted font-weight-medium capitalize">
                {{ selectedDateName }}
              </span>
            </div>
          </div>
          <v-btn icon dark @click="dialogOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-sheet>

        <!-- Dialog Body -->
        <v-card-text class="pa-6">
          <div v-if="daysData[selectedDate] && daysData[selectedDate].loading" class="d-flex flex-column align-center justify-center py-8">
            <v-progress-circular indeterminate color="primary" size="32" width="3"></v-progress-circular>
            <span class="text-caption grey--text text--darken-1 mt-3 font-weight-medium">Cargando información del día...</span>
          </div>

          <template v-else-if="daysData[selectedDate]">
            <!-- Estado A: Con transacciones -->
            <div v-if="daysData[selectedDate].hasData">
              <!-- Pagos List -->
              <div v-if="daysData[selectedDate].pagos.length > 0" class="mb-4">
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-3 mb-2 d-flex align-center">
                  <v-icon small color="primary" class="mr-1">mdi-wallet</v-icon>
                  Ingresos por Métodos de Pago
                </div>
                
                <div
                  v-for="(pago, pIndex) in daysData[selectedDate].pagos"
                  :key="pIndex"
                  class="pago-item-card mb-2 pa-3 rounded-lg d-flex flex-column"
                  :style="getPaymentRowStyle(pago.forma_pago_id)"
                >
                  <div class="d-flex justify-space-between align-center">
                    <span class="d-flex align-center font-weight-bold" style="font-size: 0.8rem;">
                      <v-icon small :color="getPaymentColor(pago.forma_pago_id)" class="mr-2">
                        {{ getPaymentIcon(pago.forma_pago_id) }}
                      </v-icon>
                      {{ formaPagoNombre(pago.forma_pago_id) }}
                    </span>
                    <span class="font-weight-black text-h6 primary--text">
                      $ {{ pago.total | formatToTwoDecimals }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Subsidios Box -->
              <v-card
                v-if="daysData[selectedDate].subsidiosKg > 0"
                class="pa-4 rounded-lg border-light"
                style="background-color: #fff9db; border: 1px solid #ffe3e3;"
                flat
              >
                <div class="d-flex align-center">
                  <v-avatar color="orange lighten-4" size="36" class="mr-3">
                    <v-icon color="orange darken-3" size="20">mdi-scale-balance</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-caption orange--text text--darken-4 font-weight-bold">
                      DESCARGAS DE SUBSIDIO
                    </div>
                    <div class="text-h6 font-weight-black orange--text text--darken-4">
                      {{ daysData[selectedDate].subsidiosKg | formatToThreeDecimals }} kg
                    </div>
                  </div>
                </div>
              </v-card>
            </div>

            <!-- Estado B: Sin transacciones (Caja no generada) -->
            <div v-else class="d-flex flex-column align-center justify-center py-6 text-center">
              <v-icon color="grey lighten-1" size="48" class="mb-3">mdi-calendar-blank-outline</v-icon>
              <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-1">Caja sin movimientos</h3>
              <p class="text-caption grey--text text--darken-1 max-w-280 mb-4">
                No se registraron transacciones financieras ni descargas de subsidios para esta fecha.
              </p>
              
              <v-btn
                color="success"
                class="text-capitalize font-weight-bold shadow-sm px-6"
                :disabled="!$can('diarios_pagos.store') || actionLoading[selectedDate]"
                :loading="actionLoading[selectedDate]"
                @click="generateBox(selectedDate)"
                rounded
              >
                <v-icon left>mdi-plus-circle</v-icon>
                Generar Caja Diaria
              </v-btn>
            </div>
          </template>
        </v-card-text>

        <!-- Dialog Footer Actions -->
        <v-divider></v-divider>
        <v-card-actions class="pa-4 bg-light d-flex justify-space-between align-center">
          <div class="d-flex align-center">
            <!-- Download Button (only if hasData) -->
            <v-btn
              v-if="daysData[selectedDate] && !daysData[selectedDate].loading && daysData[selectedDate].hasData"
              color="primary"
              outlined
              class="text-capitalize font-weight-bold mr-2"
              :disabled="actionLoading[selectedDate]"
              :loading="actionLoading[selectedDate]"
              @click="downloadTransactions(selectedDate)"
            >
              <v-icon left>mdi-download</v-icon>
              Descargar
            </v-btn>

            <!-- Recalculate/Update Button (RBAC, always available so they can force update at any time) -->
            <v-btn
              v-if="daysData[selectedDate] && !daysData[selectedDate].loading && $can('diarios_pagos.update')"
              color="orange darken-2"
              outlined
              class="text-capitalize font-weight-bold"
              :disabled="actionLoading[selectedDate]"
              :loading="actionLoading[selectedDate]"
              @click="updateBox(selectedDate)"
            >
              <v-icon left>mdi-refresh</v-icon>
              Actualizar
            </v-btn>
          </div>

          <v-btn text class="text-capitalize font-weight-bold" @click="dialogOpen = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import * as XLSX from 'xlsx';
import FormaPagoMixin from '@/mixins/FormasDePagos';

export default {
  name: 'DiariosMainComponent',
  mixins: [FormaPagoMixin],
  data() {
    return {
      focus: '',
      pickerMonth: '',
      menuOpen: false,
      visibleDates: [],
      daysData: {},
      actionLoading: {},
      loadingMonth: false,
      weekdayNames: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
      selectedDate: '',
      dialogOpen: false
    }
  },
  computed: {
    title() {
      if (!this.focus) return '';
      return this.$moment(this.focus).format('MMMM [de] YYYY');
    },
    selectedDateName() {
      if (!this.selectedDate) return '';
      return this.$moment(this.selectedDate).format('dddd, D [de] MMMM [de] YYYY');
    }
  },
  watch: {
    focus(newValue) {
      if (newValue) {
        this.pickerMonth = this.$moment(newValue).format('YYYY-MM');
      }
    }
  },
  beforeMount() {
    const today = this.$moment().format('YYYY-MM-DD');
    this.focus = today;
    this.pickerMonth = this.$moment(today).format('YYYY-MM');
    this.selectedDate = today;
  },
  created() {
    // Cargar las formas de pago si no están cargadas
    if (this.$store.getters['FormasPagos/all'].length === 0) {
      this.$store.dispatch('FormasPagos/onFetch');
    }
    
    // Escuchar cambios de sucursal
    this.$root.$on('update:sucursal_id', this.onBranchChanged);
  },
  beforeDestroy() {
    // Quitar event listener
    this.$root.$off('update:sucursal_id', this.onBranchChanged);
  },
  methods: {
    onBranchChanged() {
      this.fetchMonthData();
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },
    setToday() {
      this.focus = this.$moment().format('YYYY-MM-DD');
    },
    refreshCurrentMonth() {
      this.fetchMonthData();
    },
    isToday(dateStr) {
      return this.$moment().format('YYYY-MM-DD') === dateStr;
    },
    getDayNumber(dateStr) {
      return this.$moment(dateStr).date();
    },
    onMonthPickerInput(val) {
      this.menuOpen = false;
      this.focus = this.$moment(val, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
    },
    openDayDialog(date) {
      this.selectedDate = date;
      this.dialogOpen = true;
    },
    totalPayments(date) {
      if (!this.daysData[date] || !this.daysData[date].pagos) return 0;
      return this.daysData[date].pagos.reduce((acc, p) => acc + parseFloat(p.total), 0);
    },
    updateRange({ start, end }) {
      this.focus = start.date;
      
      // Calcular todas las fechas en el rango visible
      const startMoment = this.$moment(start.date);
      const endMoment = this.$moment(end.date);
      
      const dates = [];
      let current = this.$moment(startMoment);
      
      while (current.isSameOrBefore(endMoment)) {
        dates.push(current.format('YYYY-MM-DD'));
        current.add(1, 'day');
      }
      
      this.visibleDates = dates;
      this.fetchMonthData();
    },
    async fetchMonthData() {
      if (this.visibleDates.length === 0) return;
      this.loadingMonth = true;
      
      // Inicializar el estado de carga
      this.visibleDates.forEach(date => {
        this.$set(this.daysData, date, { loading: true, pagos: [], subsidiosKg: 0, hasData: false });
      });

      const fecha_from = this.visibleDates[0];
      const fecha_to = this.visibleDates[this.visibleDates.length - 1];

      try {
        const p1 = window.axios.get('/diarios_pagos', {
          params: {
            itemsPerPage: 1000, // Asegura que descargue todas las filas del mes
            page: 1,
            export: 0,
            fecha_from,
            fecha_to
          }
        });
        const p2 = window.axios.get('/diarios_bonos', {
          params: {
            export: 0,
            fecha_from,
            fecha_to
          }
        });
        
        const [r1, r2] = await Promise.all([p1, p2]);
        
        const allPagos = r1.data.data || [];
        const allBonos = r2.data || []; // Lista de montos de bonos por fecha
        
        // Agrupar pagos por fecha
        const pagosByDate = {};
        allPagos.forEach(p => {
          if (p.fecha) {
            const d = this.$moment(p.fecha).format('YYYY-MM-DD');
            if (!pagosByDate[d]) pagosByDate[d] = [];
            pagosByDate[d].push(p);
          }
        });
        
        // Agrupar bonos por fecha
        const bonosByDate = {};
        allBonos.forEach(b => {
          if (b.fecha) {
            const d = this.$moment(b.fecha).format('YYYY-MM-DD');
            bonosByDate[d] = b.total;
          }
        });
        
        // Asignar al estado local de cada celda
        this.visibleDates.forEach(date => {
          const pagos = pagosByDate[date] || [];
          const subsidiosKg = bonosByDate[date] || 0;
          const hasData = pagos.length > 0 || subsidiosKg > 0;
          
          this.$set(this.daysData, date, {
            loading: false,
            pagos,
            subsidiosKg,
            hasData
          });
        });
      } catch (error) {
        console.error('Error fetching month data:', error);
        this.visibleDates.forEach(date => {
          this.$set(this.daysData, date, {
            loading: false,
            pagos: [],
            subsidiosKg: 0,
            hasData: false,
            error: true
          });
        });
      } finally {
        this.loadingMonth = false;
      }
    },
    async fetchDayData(date) {
      // Método individual para recarga de una celda específica tras generar/actualizar
      try {
        const p1 = window.axios.get('/diarios_pagos', {
          params: {
            itemsPerPage: 50,
            page: 1,
            export: 0,
            fecha_from: date,
            fecha_to: date
          }
        });
        const p2 = window.axios.get('/diarios_bonos', {
          params: {
            export: 0,
            fecha_from: date,
            fecha_to: date
          }
        });
        
        const [r1, r2] = await Promise.all([p1, p2]);
        
        const pagos = r1.data.data || [];
        const subsidiosKg = r2.data.total || 0;
        const hasData = pagos.length > 0 || subsidiosKg > 0;
        
        this.$set(this.daysData, date, {
          loading: false,
          pagos,
          subsidiosKg,
          hasData
        });
      } catch (err) {
        console.error(`Error loading data for ${date}:`, err);
        this.$set(this.daysData, date, {
          loading: false,
          pagos: [],
          subsidiosKg: 0,
          hasData: false,
          error: true
        });
      }
    },
    async generateBox(date) {
      this.$set(this.actionLoading, date, true);
      try {
        this.$store.commit('DiariosCreate/setFecha', date);
        await this.$store.dispatch('DiariosCreate/onSave');
        await this.fetchDayData(date);
      } catch (e) {
        console.error(e);
      } finally {
        this.$set(this.actionLoading, date, false);
      }
    },
    async updateBox(date) {
      this.$set(this.actionLoading, date, true);
      try {
        this.$store.commit('DiariosUpdate/setFecha', date);
        await this.$store.dispatch('DiariosUpdate/onSave');
        await this.fetchDayData(date);
      } catch (e) {
        console.error(e);
      } finally {
        this.$set(this.actionLoading, date, false);
      }
    },
    async downloadTransactions(date) {
      this.$set(this.actionLoading, date, true);
      try {
        this.$store.commit('TransaccionesExport/setFecha', date);
        const response = await this.$store.dispatch('TransaccionesExport/onSave');
        
        const data = {
          'debitos': response.debitos || [],
          'creditos': response.creditos || [],
          'bonos': response.bonos || []
        };
        
        const workbook = XLSX.utils.book_new();
        const ws1 = XLSX.utils.json_to_sheet(data.debitos);
        XLSX.utils.book_append_sheet(workbook, ws1, 'debitos');
        const ws2 = XLSX.utils.json_to_sheet(data.creditos);
        XLSX.utils.book_append_sheet(workbook, ws2, 'creditos');
        const ws3 = XLSX.utils.json_to_sheet(data.bonos);
        XLSX.utils.book_append_sheet(workbook, ws3, 'bonos');
        XLSX.writeFile(workbook, `transacciones-${date}.xlsx`);
      } catch (e) {
        console.error(e);
      } finally {
        this.$set(this.actionLoading, date, false);
      }
    },
    getPaymentColor(formaPagoId) {
      const name = this.formaPagoNombre(formaPagoId).toLowerCase();
      if (name.includes('efectivo')) return 'green darken-2';
      if (name.includes('tarjeta') || name.includes('débito') || name.includes('crédito')) return 'indigo darken-2';
      if (name.includes('transferencia') || name.includes('banco') || name.includes('depósito')) return 'cyan darken-3';
      return 'grey darken-3';
    },
    getPaymentIcon(formaPagoId) {
      const name = this.formaPagoNombre(formaPagoId).toLowerCase();
      if (name.includes('efectivo')) return 'mdi-cash';
      if (name.includes('tarjeta') || name.includes('débito') || name.includes('crédito')) return 'mdi-credit-card';
      if (name.includes('transferencia') || name.includes('banco') || name.includes('depósito')) return 'mdi-bank-transfer';
      return 'mdi-wallet';
    },
    getPaymentRowStyle(formaPagoId) {
      const name = this.formaPagoNombre(formaPagoId).toLowerCase();
      if (name.includes('efectivo')) return { backgroundColor: '#eefcf5', color: '#1b5e20' };
      if (name.includes('tarjeta') || name.includes('débito') || name.includes('crédito')) return { backgroundColor: '#eef2ff', color: '#1a237e' };
      if (name.includes('transferencia') || name.includes('banco') || name.includes('depósito')) return { backgroundColor: '#e0f7fa', color: '#006064' };
      return { backgroundColor: '#f5f5f5', color: '#212121' };
    }
  }
}
</script>

<style scoped>
.calendar-header-banner {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: inherit;
}

.text-white-muted {
  color: rgba(255, 255, 255, 0.75);
}

.border-white-op {
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
}

.border-white-op:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.04) !important;
}

.shadow-sm-hover:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.08) !important;
}

.calendar-day-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.day-num-badge {
  font-size: 0.75rem;
  font-weight: 800;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: #34495e;
  background: #f1f3f5;
}

.is-today-badge {
  background-color: #f59f00 !important;
  color: #ffffff !important;
}

.today-tag {
  font-size: 0.65rem;
  font-weight: 800;
  padding: 1px 6px;
  border-radius: 4px;
  background-color: #f59f00;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.pago-item-card {
  font-size: 0.7rem;
  border: 1px solid rgba(0,0,0,0.02);
  transition: all 0.2s ease;
}

.pago-item-card:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.subsidio-pill {
  font-size: 0.7rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 4px;
  background-color: #fff9db;
  color: #e67e22;
  border: 1px solid #ffe3e3;
}

.cell-action-bar {
  border-top: 1px dashed rgba(0,0,0,0.05);
}

.bg-light-blue {
  background-color: #ebf5ff !important;
}

.bg-light-orange {
  background-color: #fff4e6 !important;
}

.border-light {
  border: 1px solid rgba(0, 0, 0, 0.03) !important;
}

.btn-generate-action {
  font-size: 0.65rem !important;
  height: 22px !important;
}

.no-mov-text {
  font-size: 0.68rem;
  color: #a0aec0;
  font-weight: 500;
}

.capitalize {
  text-transform: capitalize;
}

.italic {
  font-style: italic;
}

.select-month-trigger {
  border-bottom: 2px dashed rgba(255, 255, 255, 0.4);
  padding-bottom: 2px;
  transition: border-color 0.2s ease;
}

.select-month-trigger:hover {
  border-color: rgba(255, 255, 255, 0.9);
}

.cursor-pointer {
  cursor: pointer;
}

.max-w-280 {
  max-width: 280px;
}

.cell-summary-row {
  font-size: 0.72rem;
  padding: 3px 6px;
  background-color: #f8fafc;
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.02);
  margin-bottom: 4px;
}
</style>
